import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@krea/shared/design-system/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import { cn } from "@krea/shared/utils";

export interface SectionNavItem {
  id: string; // must match the section element id in the DOM
  label: string;
  icon?: LucideIcon;
}

interface Props {
  items: SectionNavItem[];
  /**
   * Sticky offset in px (if you have a fixed top header, set e.g. 64)
   * Used for intersection observer + scroll behavior.
   */
  offsetTopPx?: number;
  /**
   * Default active id if nothing is visible yet
   */
  defaultActiveId?: string;
  className?: string;
  /**
   * Optional: CSS selector for the scroll container (if not scrolling the window)
   * e.g., ".my-scroll-container"
   */
  scrollContainerSelector?: string;
}

function scrollToSection(
  id: string,
  offsetTopPx: number,
  scrollContainerSelector?: string,
): void {
  const el = document.getElementById(id);
  if (!el) return;

  // Find the scroll container
  const scrollContainer = scrollContainerSelector
    ? document.querySelector<HTMLElement>(scrollContainerSelector)
    : null;

  if (scrollContainer) {
    // Scrolling within a container
    const containerRect = scrollContainer.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const scrollTop =
      scrollContainer.scrollTop +
      (elRect.top - containerRect.top) -
      offsetTopPx -
      12;

    scrollContainer.scrollTo({ top: scrollTop, behavior: "smooth" });
  } else {
    // Scrolling the window
    const rect = el.getBoundingClientRect();
    const top = window.scrollY + rect.top - offsetTopPx - 12;
    window.scrollTo({ top, behavior: "smooth" });
  }

  // update hash without jump
  window.history.replaceState(null, "", `#${id}`);
}

export const InnerPageNavigation = ({
  items,
  offsetTopPx = 0,
  defaultActiveId,
  className,
  scrollContainerSelector,
}: Props) => {
  const validIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  const initial = useMemo(() => {
    const fromHash =
      typeof window !== "undefined"
        ? window.location.hash.replace("#", "")
        : "";
    if (fromHash && validIds.has(fromHash)) return fromHash;
    if (defaultActiveId && validIds.has(defaultActiveId))
      return defaultActiveId;

    return items[0]?.id ?? "";
  }, [defaultActiveId, items, validIds]);

  const [activeId, setActiveId] = useState<string>(initial);

  // Avoid observer flapping when user clicks nav and smooth-scrolls
  const clickLockRef = useRef<number | null>(null);

  useEffect(() => {
    if (items.length === 0) return;

    const elements = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (elements.length === 0) return;

    // Find the scroll container (or use window)
    const scrollContainer = scrollContainerSelector
      ? document.querySelector<HTMLElement>(scrollContainerSelector)
      : null;

    const observer = new IntersectionObserver(
      (entries) => {
        // If user just clicked, let the scroll settle
        if (clickLockRef.current) return;

        // pick the nearest visible section (highest intersection ratio, or closest to top)
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
          window.history.replaceState(null, "", `#${visible[0].target.id}`);
        }
      },
      {
        // rootMargin shifts the "active zone" below your fixed header
        root: scrollContainer, // Use the scroll container if specified
        threshold: [0.2, 0.35, 0.5, 0.75],
        rootMargin: `-${offsetTopPx + 8}px 0px -60% 0px`,
      },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [items, offsetTopPx, scrollContainerSelector]);

  const onNavigate = (id: string): void => {
    setActiveId(id);

    // lock observer updates briefly during smooth scroll
    clickLockRef.current = window.setTimeout(() => {
      clickLockRef.current = null;
    }, 700);

    scrollToSection(id, offsetTopPx, scrollContainerSelector);
  };

  return (
    <>
      {/* Mobile: dropdown */}
      <div className="block md:hidden">
        <Select value={activeId} onValueChange={onNavigate}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Jump to…" />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop: sticky nav */}
      <nav
        className={cn(
          "hidden sticky space-y-1 md:block",
          className,
        )}
        style={{ top: `${offsetTopPx}px` }}
        aria-label="Section navigation"
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === activeId;

          return (
            <Button
              key={item.id}
              type="button"
              variant="ghost"
              onClick={() => onNavigate(item.id)}
              className={cn(
                "w-full justify-start gap-3 rounded-md px-3 py-2 text-sm font-medium",
                isActive
                  ? "bg-primary-150 text-primary"
                  : "text-primary-400 hover:bg-primary-150 hover:text-primary",
              )}
            >
              {Icon ? <Icon className="h-4 w-4" /> : null}
              <span>{item.label}</span>
            </Button>
          );
        })}
      </nav>
    </>
  );
};
